import imageCompression from 'browser-image-compression'
import { useTranslationsStore } from '@/stores/translations.js'

/**
 * Composable for handling image file validation and compression
 * @returns {Object} Image compression utilities
 */
export function useImageCompression() {
    const { t } = useTranslationsStore()
    /**
     * Validates and compresses an image file if needed
     * @param {File} file - The image file to process
     * @param {Object} options - Compression options
     * @param {number} options.maxSizeMB - Maximum file size in MB before compression (default: 5)
     * @param {number} options.targetSizeMB - Target size after compression (default: 4.5)
     * @param {number} options.maxWidthOrHeight - Maximum width or height (default: 1920)
     * @returns {Promise<{success: boolean, file?: File, error?: string}>}
     */
    async function processImage(file, options = {}) {
        const { maxSizeMB = 1.5, targetSizeMB = 1, maxWidthOrHeight = 512 } = options

        // Validate file type
        if (!file.type.startsWith('image/')) {
            return {
                success: false,
                error: t('image_upload.invalid_file'),
            }
        }

        let processedFile = file
        const fileSizeInMB = file.size / 1024 / 1024

        // Compress image if it's larger than maxSizeMB
        if (file.size > maxSizeMB * 1024 * 1024) {
            try {
                const compressionOptions = {
                    maxSizeMB: targetSizeMB,
                    maxWidthOrHeight,
                    useWebWorker: true,
                }

                processedFile = await imageCompression(file, compressionOptions)

                const compressedSizeInMB = processedFile.size / 1024 / 1024
                console.log(
                    `Image compressed from ${fileSizeInMB.toFixed(2)}MB to ${compressedSizeInMB.toFixed(2)}MB`,
                )
            } catch (error) {
                console.error('Image compression failed:', error)
                return {
                    success: false,
                    error: t('image_upload.compression_failed'),
                }
            }
        }

        return {
            success: true,
            file: processedFile,
        }
    }

    /**
     * Creates a data URL preview from a file
     * @param {File} file - The file to create a preview from
     * @returns {Promise<string>} Data URL of the image
     */
    function createPreview(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = (e) => resolve(e.target.result)
            reader.onerror = reject
            reader.readAsDataURL(file)
        })
    }

    return {
        processImage,
        createPreview,
    }
}
